import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useStore } from 'zustand';
import { patchUsersMeSettingsMutation } from '@citylytics/core-api-ts-client/react-query';
import { applySavedLanguage, changeDeviceLanguage } from '@citylytics/web-i18n/i18n/service';
import { createSaveTracker } from './session.utils.js';
// One tracker across every control: each has its own mutation, and their saves overlap.
const settingSaves = createSaveTracker();
/** The keys' values from `source`, laid over the signed-in user's current settings. */
function applySettings(params) {
    const { store, keys, source } = params;
    const { user, setUser } = store.getState();
    if (!user || keys.length === 0) {
        return;
    }
    setUser({
        ...user,
        settings: { ...user.settings, ...Object.fromEntries(keys.map((key) => [key, source[key]])) },
    });
}
/**
 * Saves the signed-in user's settings. Each change applies at once and is put back if the save
 * fails, so the app never waits on the network; `status` says whether the last one saved. Only
 * the settings a save sent, and only while it is their newest save, are touched when it answers.
 */
export function useSaveUserSettings(params) {
    const { store, onSaveFailed } = params;
    const saveSettings = useMutation({
        ...patchUsersMeSettingsMutation(),
        onMutate: ({ body = {} }) => {
            const keys = Object.keys(body);
            const saveId = settingSaves.start(keys);
            const previous = { ...store.getState().user?.settings };
            applySettings({ store, keys, source: body });
            return { saveId, keys, previous };
        },
        onSuccess: (response, _variables, context) => {
            applySettings({ store, keys: settingSaves.keysOwnedBy(context), source: response.data });
        },
        onError: (_error, _variables, context) => {
            if (context) {
                applySettings({ store, keys: settingSaves.keysOwnedBy(context), source: context.previous });
            }
            onSaveFailed?.();
        },
    });
    return {
        save: (settings) => saveSettings.mutate({ body: settings }),
        /** `idle` before any change. */
        status: saveSettings.status,
    };
}
/**
 * Shows the language saved on the signed-in account, on login and whenever it changes. Mounted
 * once, at the app's root; signed out, the device's choice stays.
 */
export function useSavedLanguage(store) {
    const savedLanguage = useStore(store, (state) => state.user?.settings.language ?? null);
    useEffect(() => {
        applySavedLanguage(savedLanguage);
    }, [savedLanguage]);
}
/**
 * The account's language and how to change it. Signed in, a change is saved so every app and
 * device opens in it, and shows at once; null ("As on device") shows the device's language. Signed
 * out there is no account, so the choice is the device's.
 */
export function useLanguageSetting(params) {
    const { store } = params;
    const savedLanguage = useStore(store, (state) => state.user?.settings.language ?? null);
    const isSignedIn = useStore(store, (state) => state.user !== null);
    const { save, status } = useSaveUserSettings(params);
    function changeLanguage(language) {
        if (!isSignedIn) {
            if (language)
                changeDeviceLanguage(language);
            return;
        }
        // The save's optimistic update changes `savedLanguage`, which `useSavedLanguage` shows too;
        // applying here as well means the switch doesn't wait for a re-render.
        applySavedLanguage(language);
        save({ language });
    }
    return { savedLanguage, changeLanguage, saveStatus: status };
}
